import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  LOCALE_OPTIONS,
  getBrowserLocale,
  getCountryOption,
  getLocaleOption,
  getLocalesForCountry,
  localeToUiLanguage,
  resolveLocaleContext,
  type SupportedCountryCode,
} from "@/domain/locale/catalog";

const HIDDEN_PREFIXES = ["/auth", "/legal", "/invite"];

export function LocaleOnboardingDialog() {
  const location = useLocation();
  const { t, setLanguage } = useLanguage();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [country, setCountry] = useState<SupportedCountryCode>("PT");
  const [locale, setLocale] = useState("pt-PT");
  const [currency, setCurrency] = useState("EUR");

  const localeOptions = useMemo(() => getLocalesForCountry(country), [country]);
  const hidden = HIDDEN_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  useEffect(() => {
    if (hidden) {
      setOpen(false);
      setLoading(false);
      return;
    }

    let active = true;

    const inspectSession = async () => {
      setLoading(true);
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!active) return;

      if (!session?.user) {
        setUserId(null);
        setOrganizationId(null);
        setOpen(false);
        setLoading(false);
        return;
      }

      setUserId(session.user.id);

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("organization_id, country, user_locale")
        .eq("user_id", session.user.id)
        .single();

      if (!active) return;

      if (profileError || !profileData?.organization_id) {
        setOpen(false);
        setLoading(false);
        return;
      }

      setOrganizationId(profileData.organization_id);

      const { data: orgData } = await supabase
        .from("organizations")
        .select("country, currency")
        .eq("id", profileData.organization_id)
        .single();

      if (!active) return;

      const resolved = resolveLocaleContext({
        profileLocale: profileData.user_locale,
        profileCountry: profileData.country,
        organizationCountry: orgData?.country,
        organizationCurrency: orgData?.currency,
        browserLocale: getBrowserLocale(),
      });

      setCountry(resolved.userCountry);
      setLocale(resolved.userLocale);
      setCurrency(orgData?.currency || resolved.userCurrency);
      setOpen(!profileData.user_locale);
      setLoading(false);
    };

    void inspectSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void inspectSession();
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [hidden]);

  useEffect(() => {
    const countryOption = getCountryOption(country);
    const matchingLocale = getLocaleOption(locale);
    if (!countryOption) return;

    if (!matchingLocale || matchingLocale.country !== country) {
      setLocale(countryOption.locale);
    }

    setCurrency(countryOption.currency);
  }, [country, locale]);

  const handleSave = async () => {
    if (!userId || !organizationId) return;

    setSaving(true);
    try {
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          country,
          user_locale: locale,
        })
        .eq("user_id", userId);

      if (profileError) throw profileError;

      const { error: organizationError } = await supabase
        .from("organizations")
        .update({
          country,
          currency,
        })
        .eq("id", organizationId);

      if (organizationError) throw organizationError;

      const uiLanguage = localeToUiLanguage(locale);
      localStorage.setItem("orca-language", uiLanguage);
      setLanguage(uiLanguage);
      window.dispatchEvent(new Event("orzio-profile-updated"));
      setOpen(false);
      toast({
        title: t("onboarding.locale.savedTitle"),
        description: t("onboarding.locale.savedDescription"),
      });
    } catch (cause) {
      toast({
        title: t("common.error"),
        description: cause instanceof Error ? cause.message : t("onboarding.locale.saveError"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || hidden) return null;

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-md [&>button]:hidden">
        <DialogHeader>
          <DialogTitle>{t("onboarding.locale.title")}</DialogTitle>
          <DialogDescription>{t("onboarding.locale.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{t("settings.country")}</Label>
            <Select value={country} onValueChange={(value) => setCountry(value as SupportedCountryCode)}>
              <SelectTrigger>
                <SelectValue placeholder={t("onboarding.locale.countryPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {Array.from(new Set(LOCALE_OPTIONS.map((option) => option.country))).map((value) => {
                  const option = getCountryOption(value);
                  return (
                    <SelectItem key={value} value={value}>
                      {option?.countryLabel ?? value}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("settings.language")}</Label>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger>
                <SelectValue placeholder={t("onboarding.locale.languagePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {localeOptions.map((option) => (
                  <SelectItem key={option.locale} value={option.locale}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("settings.currency")}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger>
                <SelectValue placeholder={t("onboarding.locale.currencyPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {Array.from(new Set(localeOptions.map((option) => option.currency))).map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("onboarding.locale.continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
